<template>
  <div class="storage-files-container">
    <div class="storage-files-header">
      <div class="header-left">
        <el-button icon="el-icon-back" @click="goBack">返回</el-button>
        <h2>{{ bucketName }} - 文件管理</h2>
      </div>
      <el-upload
        v-if="$canWrite()"
        :action="uploadUrl"
        :headers="uploadHeaders"
        :on-success="handleUploadSuccess"
        :on-error="handleUploadError"
        :show-file-list="false"
        :before-upload="beforeUpload"
      >
        <el-button type="primary" icon="el-icon-upload">上传文件</el-button>
      </el-upload>
    </div>

    <!-- 文件列表 -->
    <el-card shadow="hover" class="storage-files-card">
      <el-table :data="paginatedFiles" style="width: 100%" v-loading="loading" @selection-change="handleSelectionChange">
        <el-table-column v-if="$canWrite()" type="selection" width="55"></el-table-column>
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="name" label="文件名" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <div class="file-name">
              <i :class="getFileIcon(scope.row.extension)"></i>
              <span class="file-name-text">{{ scope.row.name }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="size" label="大小" width="120">
          <template slot-scope="scope">
            {{ formatFileSize(scope.row.size) }}
          </template>
        </el-table-column>
        <el-table-column prop="mime_type" label="类型" width="150"></el-table-column>
        <el-table-column label="文件链接" min-width="200">
          <template slot-scope="scope">
            <div class="file-url">
              <el-input 
                :value="fileUrls[scope.row.id] || '加载中...'" 
                readonly 
                size="small"
              >
                <el-button 
                  slot="append" 
                  icon="el-icon-document-copy" 
                  @click="copyFileUrl(scope.row)"
                  title="复制链接"
                ></el-button>
              </el-input>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="上传时间" width="180"></el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button type="success" size="mini" icon="el-icon-view" @click="previewFile(scope.row)">预览</el-button>
              <el-button type="primary" size="mini" icon="el-icon-download" @click="downloadFile(scope.row)">下载</el-button>
              <el-button v-if="$canWrite()" type="danger" size="mini" icon="el-icon-delete" @click="handleDelete(scope.row)">删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页固定在底部 -->
      <div class="pagination-footer">
        <div class="footer-left">
          <el-button
            v-if="$canWrite()"
            type="danger"
            size="small"
            :disabled="selectedFiles.length === 0"
            @click="batchDelete"
          >
            批量删除 ({{ selectedFiles.length }})
          </el-button>
        </div>
        
        <el-pagination
          @current-change="handlePageChange"
          :current-page="pagination.currentPage"
          :page-size="pagination.pageSize"
          layout="total, prev, pager, next, jumper"
          :total="totalFiles"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 文件预览对话框 -->
    <el-dialog title="文件预览" :visible.sync="previewDialogVisible" width="70%">
      <div class="preview-container">
        <img v-if="isImage(currentPreviewFile)" :src="previewUrl" alt="预览" style="max-width: 100%;">
        <video v-else-if="isVideo(currentPreviewFile)" :src="previewUrl" controls style="max-width: 100%;"></video>
        <audio v-else-if="isAudio(currentPreviewFile)" :src="previewUrl" controls style="width: 100%;"></audio>
        <div v-else class="no-preview">
          <i class="el-icon-document" style="font-size: 64px; color: #909399;"></i>
          <p>此文件类型不支持预览</p>
          <el-button type="primary" @click="downloadFile(currentPreviewFile)">下载文件</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import Cookies from 'js-cookie'

export default {
  name: 'StorageFiles',
  data() {
    return {
      projectId: null,
      bucketId: null,
      bucketName: '',
      files: [],
      selectedFiles: [], // 选中的文件
      loading: false,
      previewDialogVisible: false,
      currentPreviewFile: null,
      previewUrl: '',
      fileUrls: {}, // 存储文件 URL 的映射
      // 分页
      pagination: {
        currentPage: 1,
        pageSize: 20
      }
    }
  },
  computed: {
    uploadUrl() {
      const token = Cookies.get('token')
      return `${this.$backendUrl}/api/projects/${this.projectId}/buckets/${this.bucketId}/files?token=${token}`
    },
    uploadHeaders() {
      return {
        'Authorization': `Bearer ${Cookies.get('token')}`
      }
    },
    paginatedFiles() {
      const start = (this.pagination.currentPage - 1) * this.pagination.pageSize;
      const end = start + this.pagination.pageSize;
      return this.files.slice(start, end);
    },
    totalFiles() {
      return this.files.length;
    }
  },
  created() {
    this.projectId = this.$route.params.id
    this.bucketId = this.$route.params.bucketId
    this.bucketName = this.$route.query.name || '存储桶'
    this.fetchFiles()
  },
  methods: {
    async fetchFiles() {
      this.loading = true
      try {
        const response = await this.$axios.get(`/projects/${this.projectId}/buckets/${this.bucketId}/files`)
        this.files = response.data || []
        
        // 获取每个文件的 URL
        for (const file of this.files) {
          try {
            const fileResponse = await this.$axios.get(`/projects/${this.projectId}/buckets/${this.bucketId}/files/${file.id}`)
            // 使用全局配置的后端 URL
            this.$set(this.fileUrls, file.id, this.$backendUrl + fileResponse.data.url)
          } catch (error) {
            console.error(`获取文件 ${file.id} 的 URL 失败:`, error)
          }
        }
      } catch (error) {
        console.error('获取文件列表失败:', error)
        this.$message.error('获取文件列表失败: ' + (error.response?.data?.error || error.message))
      } finally {
        this.loading = false
      }
    },
    beforeUpload(file) {
      const isLt100M = file.size / 1024 / 1024 < 100
      if (!isLt100M) {
        this.$message.error('上传文件大小不能超过 100MB!')
      }
      return isLt100M
    },
    handleUploadSuccess() {
      this.$message.success('文件上传成功')
      this.fetchFiles()
    },
    handleUploadError(error) {
      console.error('文件上传失败:', error)
      let errorMsg = '文件上传失败'
      
      // 尝试解析错误信息
      if (error.message) {
        errorMsg += ': ' + error.message
      }
      
      // 如果有响应数据，尝试获取详细错误
      try {
        const response = JSON.parse(error.message)
        if (response.error) {
          errorMsg = '文件上传失败: ' + response.error
        } else if (response.message) {
          errorMsg = '文件上传失败: ' + response.message
        }
      } catch (e) {
        // 忽略 JSON 解析错误
      }
      
      this.$message.error(errorMsg)
    },
    async downloadFile(file) {
      try {
        const response = await this.$axios.get(
          `/projects/${this.projectId}/buckets/${this.bucketId}/files/${file.id}/download`,
          { responseType: 'blob' }
        )
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', file.name)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('文件下载失败:', error)
        this.$message.error('文件下载失败')
      }
    },
    async previewFile(file) {
      try {
        const response = await this.$axios.get(`/projects/${this.projectId}/buckets/${this.bucketId}/files/${file.id}`)
        this.currentPreviewFile = file
        this.previewUrl = response.data.url
        this.previewDialogVisible = true
      } catch (error) {
        console.error('获取文件信息失败:', error)
        this.$message.error('获取文件信息失败')
      }
    },
    async copyFileUrl(file) {
      const fileUrl = this.fileUrls[file.id]
      
      if (!fileUrl || fileUrl === '加载中...') {
        this.$message.warning('文件链接还在加载中，请稍后再试')
        return
      }
      
      // 使用 Clipboard API 复制链接
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(fileUrl)
          this.$message.success('文件链接已复制到剪贴板')
        } catch (err) {
          this.fallbackCopy(fileUrl)
        }
      } else {
        this.fallbackCopy(fileUrl)
      }
    },
    fallbackCopy(text) {
      // 降级方案：使用传统方法
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      
      try {
        document.execCommand('copy')
        this.$message.success('文件链接已复制到剪贴板')
      } catch (err) {
        this.$message.error('复制失败，请手动复制')
        console.error('复制失败:', err)
      }
      
      document.body.removeChild(textarea)
    },
    isImage(file) {
      if (!file) return false
      const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp']
      return imageTypes.includes(file.extension?.toLowerCase())
    },
    isVideo(file) {
      if (!file) return false
      const videoTypes = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm']
      return videoTypes.includes(file.extension?.toLowerCase())
    },
    isAudio(file) {
      if (!file) return false
      const audioTypes = ['mp3', 'wav', 'ogg', 'aac', 'flac']
      return audioTypes.includes(file.extension?.toLowerCase())
    },
    async handleDelete(file) {
      this.$confirm(`确定要删除文件 "${file.name}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await this.$axios.delete(`/projects/${this.projectId}/buckets/${this.bucketId}/files/${file.id}`)
          this.$message.success('文件删除成功')
          this.fetchFiles()
        } catch (error) {
          console.error('文件删除失败:', error)
          this.$message.error('文件删除失败')
        }
      }).catch(() => {
        this.$message.info('已取消删除')
      })
    },
    goBack() {
      this.$router.push(`/space/${this.projectId}/storage`)
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },
    getFileIcon(extension) {
      const iconMap = {
        'pdf': 'el-icon-document',
        'doc': 'el-icon-document',
        'docx': 'el-icon-document',
        'xls': 'el-icon-document',
        'xlsx': 'el-icon-document',
        'ppt': 'el-icon-document',
        'pptx': 'el-icon-document',
        'txt': 'el-icon-document',
        'jpg': 'el-icon-picture',
        'jpeg': 'el-icon-picture',
        'png': 'el-icon-picture',
        'gif': 'el-icon-picture',
        'bmp': 'el-icon-picture',
        'svg': 'el-icon-picture',
        'mp4': 'el-icon-video-camera',
        'avi': 'el-icon-video-camera',
        'mov': 'el-icon-video-camera',
        'mp3': 'el-icon-headset',
        'wav': 'el-icon-headset',
        'zip': 'el-icon-folder',
        'rar': 'el-icon-folder',
        '7z': 'el-icon-folder'
      }
      return iconMap[extension?.toLowerCase()] || 'el-icon-document'
    },
    
    // 页码改变
    handlePageChange(page) {
      this.pagination.currentPage = page;
    },
    
    // 处理表格多选
    handleSelectionChange(selection) {
      this.selectedFiles = selection;
    },
    
    // 批量删除文件
    async batchDelete() {
      if (this.selectedFiles.length === 0) {
        this.$message.warning('请先选择要删除的文件');
        return;
      }

      try {
        await this.$confirm(`确定要删除选中的 ${this.selectedFiles.length} 个文件吗？此操作不可恢复！`, '警告', {
          type: 'warning',
          confirmButtonText: '确定删除',
          cancelButtonText: '取消'
        });
        
        const ids = this.selectedFiles.map(file => file.id);
        
        await this.$axios.post(`/projects/${this.projectId}/buckets/${this.bucketId}/files/batch-delete`, {
          ids: ids
        });
        
        this.$message.success(`成功删除 ${ids.length} 个文件`);
        this.selectedFiles = [];
        this.fetchFiles();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('批量删除失败:', error);
          this.$message.error('批量删除失败: ' + (error.response?.data?.error || error.message));
        }
      }
    }
  }
}
</script>

<style scoped>
.storage-files-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.storage-files-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-left h2 {
  margin: 0;
  color: #303133;
}

.storage-files-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.storage-files-card >>> .el-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
}

.storage-files-card >>> .el-table {
  flex: 1;
  overflow-y: auto;
}

/* 分页固定在底部 */
.pagination-footer {
  padding: 16px;
  border-top: 1px solid #e4e7ed;
  background: white;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  gap: 16px;
}

.footer-left {
  margin-right: auto;
}

.file-name {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
}

.file-name i {
  font-size: 18px;
  color: #409EFF;
  flex-shrink: 0;
}

.file-name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.action-buttons .el-button {
  margin: 0;
  padding: 5px 8px;
}

.preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.no-preview {
  text-align: center;
}

.no-preview p {
  margin: 20px 0;
  color: #909399;
  font-size: 16px;
}

.file-url {
  width: 100%;
}

.file-url .el-input {
  font-size: 12px;
}

.file-url .el-input__inner {
  font-family: monospace;
}
</style>
